import { useMemo } from 'react';
import { useObservable, useSubscription } from 'observable-hooks';
import type { BehaviorSubject } from 'rxjs';
import { Subject } from 'rxjs';
import {
    createAllMeasurementsClick$,
    createFilterClickActionFactory,
    createToNowClick$,
    createToNowLongPress$,
    filterActionEffects,
} from './filterActionEffects';
import { useLongPress } from './hooks/useLongPress';
import {
    TimeFilter,
    type FilterActionFactory,
    type DataSetAdapter,
    type GraphAdapter,
} from './types';

const TO_NOW_LONG_PRESS_MS = 2000;

interface Props {
    dataSet: DataSetAdapter;
    graph: GraphAdapter;
    showDateRangePicker$: BehaviorSubject<boolean>;
}

type FilterButtonConfig = {
    label: () => string;
    actionFactory: FilterActionFactory;
    longPressActionFactory?: FilterActionFactory;
};

const FILTER_BUTTONS: ReadonlyArray<FilterButtonConfig> = [
    {
        label: () => gettext('To now'),
        actionFactory: createToNowClick$,
        longPressActionFactory: createToNowLongPress$,
    },
    {
        label: () => gettext('Minutes'),
        actionFactory: createFilterClickActionFactory(TimeFilter.FiveMinute),
    },
    {
        label: () => gettext('Hour'),
        actionFactory: createFilterClickActionFactory(TimeFilter.Hour),
    },
    {
        label: () => gettext('Day'),
        actionFactory: createFilterClickActionFactory(TimeFilter.Day),
    },
    {
        label: () => gettext('Week'),
        actionFactory: createFilterClickActionFactory(TimeFilter.Week),
    },
    {
        label: () => gettext('Month'),
        actionFactory: createFilterClickActionFactory(TimeFilter.Month),
    },
    {
        label: () => gettext('Year'),
        actionFactory: createFilterClickActionFactory(TimeFilter.Year),
    },
    {
        label: () => gettext('ALL_MEASUREMENTS'),
        actionFactory: createAllMeasurementsClick$,
    },
    {
        label: () => gettext('Date picker'),
        actionFactory: createFilterClickActionFactory(TimeFilter.DatePicker),
    },
];

export function GraphTimeFilters({ dataSet, graph, showDateRangePicker$ }: Props): JSX.Element {
    const actionFactory$ = useMemo(() => new Subject<FilterActionFactory>(), []);
    const effects$ = useObservable(
        () =>
            filterActionEffects({
                dataSet,
                graph,
                actionFactory$,
                showDateRangePicker$,
            }),
        [actionFactory$, dataSet, graph, showDateRangePicker$]
    );

    useSubscription(effects$);

    const { startLongPress, cancelLongPress } = useLongPress<FilterActionFactory>(
        (longPressActionFactory) => {
            actionFactory$.next(longPressActionFactory);
        },
        TO_NOW_LONG_PRESS_MS
    );

    return (
        // Keep `view-types` for walkthrough selector compatibility.
        <div className="pure-u-1 view-types flex w-full flex-wrap items-start text-left">
            <span className="sbr-graph-filter-label">{gettext('Filter')}:</span>

            {FILTER_BUTTONS.map((button, index) => {
                const { longPressActionFactory } = button;
                return (
                    <button
                        // Static list: order and membership are fixed by design.
                        key={index}
                        type="button"
                        className="sbr-graph-filter-button"
                        onClick={() => {
                            actionFactory$.next(button.actionFactory);
                        }}
                        onMouseDown={
                            longPressActionFactory
                                ? () => startLongPress(longPressActionFactory)
                                : undefined
                        }
                        onMouseUp={longPressActionFactory ? cancelLongPress : undefined}
                        onMouseLeave={longPressActionFactory ? cancelLongPress : undefined}
                    >
                        <span>{button.label()}</span>
                    </button>
                );
            })}
        </div>
    );
}
